/**
 * Shared helper for display-value lookups.
 *
 * Wherever a page shows text (or a style) driven by a coded value from the API —
 * a status, a category, a reason, a state — the mapping lives in a map plus a
 * resolver in a module scoped to that area, never as an inline conditional in a
 * template. Each entry is `{ Label, Tone, ... }`, where Tone is the semantic
 * style bucket the component maps to a CSS class (never a raw colour — see
 * tokens.css).
 *
 * This is the one fallback rule every area shares: an unmapped code degrades to
 * the raw code with a neutral tone, so a new API value shows up visibly instead
 * of rendering blank or being silently mislabelled.
 *
 * Area modules using this: `reports2/report-display-values.js` (the reference
 * implementation), `reports/report-display-values.js`,
 * `account-admin/account-admin-display-values.js`.
 */


/**
 * Resolve a coded value against a display map.
 *
 * @param {object} map Display map keyed by the API's code.
 * @param {string} code The code to resolve.
 * @param {string} fallbackTone Tone to use when the code isn't in the map.
 * @returns {object} The map entry, or `{ Label, Tone }` built from the raw code.
 */
export function resolveDisplayValue(map, code, fallbackTone = "neutral")
{
	const entry = map[code];
	if (entry) return entry;

	return { Label: code == null || code === "" ? "—" : String(code), Tone: fallbackTone };
}


export default { resolveDisplayValue };
